import React from "react";
import PropTypes from "prop-types";
import styles from "./HistoryOverlay.module.css";

// ---- field definitions -------------------------------------------------------
// Each entry knows how to extract its value from a snapshot and render it.
// `format` receives the raw value and returns a display string (uses \n for
// multi-line values like pin lists so CSS `white-space: pre-line` wraps them).
const FIELD_DEFS = [
  { key: "title", label: "Product Name", format: (v) => v ?? "" },
  { key: "description", label: "Description", format: (v) => v ?? "" },
  { key: "author", label: "Author", format: (v) => v ?? "" },
  { key: "category", label: "Category", format: (v) => v ?? "" },
  { key: "productType", label: "Product Type", format: (v) => v ?? "" },
  {
    key: "appCategories",
    label: "WWG Store App Categories",
    format: (v) => (Array.isArray(v) ? v.join(", ") : ""),
  },
  {
    key: "thumbnailUrl",
    label: "Thumbnail Image",
    format: (v) => (v?.url ? v.url.split("/").pop() : ""),
  },
  {
    key: "productImages",
    label: "Product Images",
    format: (v) =>
      Array.isArray(v) ? `${v.length} image${v.length !== 1 ? "s" : ""}` : "",
  },
  {
    key: "publishStatus",
    label: "Status",
    format: (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : ""),
  },
  { key: "availability", label: "Availability", format: (v) => v ?? "" },
  {
    key: "preOrderStart",
    label: "Pre-Order Start",
    format: (v) => v || "",
  },
  {
    key: "preOrderEnd",
    label: "Pre-Order End",
    format: (v) => v || "",
  },
  {
    key: "publishOnDate",
    label: "Publish On Date",
    format: (v) => v || "",
  },
  {
    key: "publishOnTime",
    label: "Publish On Time",
    format: (v) => v || "",
  },
  {
    key: "unpublishOnDate",
    label: "Unpublish on this date",
    format: (v) => (v ? "True" : "False"),
  },
  {
    key: "unpublishOnDateStr",
    label: "Unpublish Date",
    format: (v) => v || "",
  },
  {
    key: "unpublishOnTimeStr",
    label: "Unpublish Time",
    format: (v) => v || "",
  },
  {
    key: "stock.currentStock",
    label: "Current Stock",
    format: (v) => (v == null ? "" : String(v)),
  },
  {
    key: "stock.vendorBin",
    label: "Vendor Bin",
    format: (v) => (v == null ? "" : String(v)),
  },
  {
    key: "stock.caseQty",
    label: "Case Qty",
    format: (v) => (v == null ? "" : String(v)),
  },
  {
    key: "stock.onHand",
    label: "On Hand",
    format: (v) => (v == null ? "" : String(v)),
  },
  {
    key: "flags.source",
    label: "Source",
    format: (v) => (v === "third_party" ? "3rd Party" : "WWG"),
  },
  {
    key: "flags.thirdPartyName",
    label: "3rd Party Name",
    format: (v) => v || "",
  },
  {
    key: "flags.isNew",
    label: "New!",
    format: (v) => (v ? "Yes" : "No"),
  },
  {
    key: "flags.markAsNewUntil",
    label: "Mark as New Until",
    format: (v) =>
      v
        ? new Date(v).toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
          })
        : "",
  },
  {
    key: "flags.isReadable",
    label: "Is Readable",
    format: (v) => (v ? "Yes" : "No"),
  },
  {
    key: "flags.isBundle",
    label: "Is Bundle",
    format: (v) => (v ? "Yes" : "No"),
  },
  {
    key: "bundlePricingMode",
    label: "Bundle Pricing Mode",
    format: (v) => v || "",
  },
  {
    key: "bundledProducts",
    label: "Bundled Products",
    format: (v) => (Array.isArray(v) ? v.map((p) => p.title || p.sku).join(", ") : ""),
  },
  {
    key: "pricing.bundledCents",
    label: "Bundled Price",
    format: (v) => (v != null ? `$${(v / 100).toFixed(2)}` : ""),
  },
  {
    key: "pricing.retailCents",
    label: "Retail Price",
    format: (v) => (v != null ? `$${(v / 100).toFixed(2)}` : ""),
  },
  {
    key: "pricing.premierMemberCents",
    label: "Premier Member Price",
    format: (v) => (v != null ? `$${(v / 100).toFixed(2)}` : ""),
  },
  {
    key: "pricing.lastPurchaseCents",
    label: "Last Purchase Price",
    format: (v) => (v != null ? `$${(v / 100).toFixed(2)}` : ""),
  },
  {
    key: "pins",
    label: "Pin Level Permissions",
    format: (v) => (Array.isArray(v) ? v.join("\n") : ""),
  },
  {
    key: "memberships",
    label: "Membership Permissions",
    format: (v) => (Array.isArray(v) ? v.join("\n") : ""),
  },
  {
    key: "tagNames",
    label: "Tags",
    format: (v) => (Array.isArray(v) ? v.join(", ") : ""),
  },
  {
    key: "audienceGroupIds",
    label: "Audience Groups",
    format: (v) => (Array.isArray(v) ? v.join(", ") : ""),
  },
  {
    key: "additionalIbos",
    label: "Additional IBOs",
    format: (v) => (Array.isArray(v) ? v.join("\n") : ""),
  },
];

// ---- helpers -----------------------------------------------------------------
function getNestedValue(obj, key) {
  return key.split(".").reduce((acc, part) => acc?.[part], obj);
}

function isEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === "object" && typeof b === "object") {
    const sortKeys = (v) => {
      if (Array.isArray(v)) return v.map(sortKeys);
      if (v && typeof v === "object") {
        return Object.keys(v)
          .sort()
          .reduce((o, k) => { o[k] = sortKeys(v[k]); return o; }, {});
      }
      return v;
    };
    return JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));
  }
  return false;
}

// ---- component ---------------------------------------------------------------
export function HistoryOverlay({
  baseline,
  current,
  onRestoreField,
  onClose,
  onUndoAll,
}) {
  const diffs = FIELD_DEFS.filter(({ key }) => {
    const baseVal = getNestedValue(baseline, key);
    const currVal = getNestedValue(current, key);
    return !isEqual(baseVal, currVal);
  });

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Field change history"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>History</h2>
          <div className={styles.headerActions}>
            {onUndoAll && diffs.length > 0 && (
              <button
                type="button"
                className={styles.undoAllButton}
                onClick={onUndoAll}
                title="Revert every field back to the last saved values."
              >
                Undo All Changes
              </button>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              title="Close this history panel."
            >
              Close
            </button>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Field</th>
                <th className={styles.th}>Original Value</th>
                <th className={styles.th}>Current Value</th>
                <th className={styles.th} />
              </tr>
            </thead>
            <tbody>
              {diffs.length === 0 ? (
                <tr>
                  <td className={styles.empty} colSpan={4}>
                    No changes since last save.
                  </td>
                </tr>
              ) : (
                diffs.map(({ key, label, format }) => {
                  const baseVal = getNestedValue(baseline, key);
                  const currVal = getNestedValue(current, key);
                  return (
                    <tr key={key} className={styles.row}>
                      <td className={styles.td}>{label}</td>
                      <td className={styles.td}>{format(baseVal)}</td>
                      <td className={styles.td}>{format(currVal)}</td>
                      <td className={styles.tdAction}>
                        <button
                          type="button"
                          className={styles.restoreButton}
                          onClick={() => onRestoreField(key, baseVal)}
                          title="Restore this field to its original saved value."
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

HistoryOverlay.propTypes = {
  baseline: PropTypes.object.isRequired,
  current: PropTypes.object.isRequired,
  onRestoreField: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUndoAll: PropTypes.func,
};

export default HistoryOverlay;
